import 'react-toggle/style.css';
import styled from 'styled-components';

import React from 'react';
import useDataSorter from 'src/CustomHooks/useDataSorter';
import IQVeraChannel from 'src/ServerEntities/IQVeraChannel';
import { ItemCell } from '../Rhapsody/RhapsodyRoutes';

import { faCaretSquareDown, faCaretSquareUp } from "@fortawesome/free-regular-svg-icons";
import { faSlidersH } from "@fortawesome/free-solid-svg-icons"; 
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';


const Table = styled.table`
    width: 100%;
`;

const ItemLine = styled.tr`
    display: flex;
    flex-direction: row;
    flex: 1 0 auto;
    width: 100%;
    justify-content: space-between;
`;

const OuterContainer = styled.div`
    border-radius: 25px;
    border: 5px solid #44A2D6;
    padding: 2px;
    flex: 1 1 auto;
`;

const InnerContainer = styled.div`
    padding-bottom: 5px;
    padding-left: 5px;
    padding-right: 5px;
`;

const TitleBar = styled.div<{ $hasFiltersExpanded: boolean }>`
    border-radius: ${props => props.$hasFiltersExpanded ? '20px 20px 0 0' : '20px'};
    background-color: #1577AE;
    padding: 5px;
    color: #FFF;
    text-align: center;
    font-size: 1.2em;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: space-between;
`;

const TitleText = styled.span`
    flex: 1;
`;

const FilterToggleButton = styled.button`
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    padding: 5px 10px;
    font-size: 1.1em;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    transition: background-color 0.2s;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
    
    &:focus {
        outline: 2px solid rgba(255, 255, 255, 0.5);
    }
`;

const FilterPanel = styled.div<{ $isExpanded: boolean }>`
    background-color: #1577AE;
    max-height: ${props => props.$isExpanded ? '200px' : '0'};
    overflow: hidden;
    transition: none; 
    border-bottom-left-radius: 15px;
    border-bottom-right-radius: 15px;
`;

const FilterContainer = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 15px;
    flex-wrap: wrap;
`;

const FilterLabel = styled.label`
    color: white;
    font-size: 0.9em;
    font-weight: normal;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const FilterDropdown = styled.select`
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #ccc;
    background-color: white;
    font-size: 0.85em;
    cursor: pointer;
    min-width: 100px;
    
    &:focus {
        outline: none;
        border-color: #44A2D6;
    }
`;

const FilterInput = styled.input`
    padding: 4px 8px;
    border-radius: 4px;
    border: 1px solid #ccc;
    font-size: 0.85em;
    min-width: 150px;
    
    &:focus {
        outline: none;
        border-color: #44A2D6;
    }
    
    &::placeholder {
        color: #999;
    }
`;

const ClearFiltersButton = styled.button`
    padding: 4px 12px;
    border-radius: 4px;
    border: 1px solid white;
    background-color: transparent;
    color: white;
    font-size: 0.85em;
    cursor: pointer;
    transition: background-color 0.2s;
    margin-left: auto;
    
    &:hover {
        background-color: rgba(255, 255, 255, 0.1);
    }
`;

const ItemHeaderCell = styled.td`
    text-align: center;
    padding: 0.1em;
    font-size: 0.9em;
    font-weight: bold;
    color: #1577AE;
    cursor: pointer;
`;

const StateBubble = styled.div`
    border-radius: 15px;
    padding: 1px;
    padding-bottom: 2px;
    text-align: center;
    font-size: 0.8em;
    font-weight: bold;
    &.ERROR  {
        background-color: #F00;
        color: #FFF;
    }
    &.STOPPED  {
        background-color: #B7BEC2;
        color: #000;
    }
    &.RUNNING  {
        background-color: #5CEA5A;
        color: #FFF;
    }
    &.STOPPING  {
        background-color: #FFA500;
        color: #FFF;
    }
    &.DEFAULT  {
        background-color: #666;
        color: #FFF;
    }
`;

const ChannelNameCell = styled(ItemCell)`
    text-align: left;
    & > div:first-child {
        font-weight: bold;
    }
    & > div:last-child {
        font-size: 0.75em;
        color: #666;
        margin-top: 2px;
        font-style: italic;
    }
`;

interface IProps {
    data: IQVeraChannel[]
};

const renderState = (state: string) => {
    const normalizedState = state?.toUpperCase() || '';
    switch (normalizedState) {
        case "ERROR": return <StateBubble className="ERROR">Errored</StateBubble>
        case "STOPPED": return <StateBubble className="STOPPED">Stopped</StateBubble>
        case "RUNNING": return <StateBubble className="RUNNING">Running</StateBubble>
        case "STOPPING": return <StateBubble className="STOPPING">Stopping</StateBubble>
        default: return <StateBubble className="DEFAULT">{state || 'Unknown'}</StateBubble>
    }
}

const formatProtocol = (messageSource: string): string => {
    if (!messageSource) return '';
    const protocolMap: { [key: string]: string } = {
        'llp_socket': 'TCP/IP',
        'http': 'HTTPS',
        'https': 'HTTPS',
        'file': 'FTP',
        'custom': 'Custom',
        'queue': 'Queue'
    };
    return protocolMap[messageSource.toLowerCase()] || messageSource.toUpperCase();
};

const formatFormat = (messageType: string): string => {
    if (!messageType) return '';
    const formatMap: { [key: string]: string } = {
        'hl7': 'HL7',
        'xml': 'XML',
        'json': 'JSON',
        'txt': 'File',
        'binary': 'Binary'
    };
    return formatMap[messageType.toLowerCase()] || messageType.toUpperCase();
};

const channelToArray = (channel: IQVeraChannel, index: number): [string, string, string, string, number, number, number, number, number, number, number] => [
    channel.name,
    channel.state,
    channel.messageSource || '',
    channel.messageType || '',
    channel.alerts,
    channel.inboundQueue || 0,
    channel.processingQueue || 0,
    channel.outboundQueue || 0,
    channel.errorQueue || 0,
    channel.completedCount || 0,
    index
];

const channelToItem = (array: [string, string, string, string, number, number, number, number, number, number, number], index: number) => {
    return <ItemLine key={`qvera-channel-${index}-${array[0]}`}>
        <ChannelNameCell width={"30%"} title={array[0]}>
            <div>{array[0]}</div>
            <div>Protocol: {formatProtocol(array[2])} | Format: {formatFormat(array[3])}</div>
        </ChannelNameCell>
        <ItemCell width={"12%"}>{renderState(array[1])}</ItemCell>
        <ItemCell width={"10%"}>{array[4]}</ItemCell>
        <ItemCell width={"10%"}>{array[5]}</ItemCell>
        <ItemCell width={"10%"}>{array[6]}</ItemCell>
        <ItemCell width={"10%"}>{array[7]}</ItemCell>
        <ItemCell width={"10%"}>{array[8]}</ItemCell>
    </ItemLine>
};

const QVeraChannels = (props: IProps) => {
    const { data } = props;
    const [sortByColumn, setSortByColumn] = React.useState(0);
    const [sortDescending, setSortDescending] = React.useState(true);
    
    const [filtersExpanded, setFiltersExpanded] = React.useState(false);
    const [protocolFilter, setProtocolFilter] = React.useState('All');
    const [statusFilter, setStatusFilter] = React.useState('All');
    const [formatFilter, setFormatFilter] = React.useState('All');
    const [searchText, setSearchText] = React.useState('');


    const changeSortColumn = (columnIndex: number) => (event: React.SyntheticEvent) => {
        event.stopPropagation();
        if (columnIndex === sortByColumn) {
            setSortDescending(!sortDescending);
        } else {
            setSortDescending(true);
        }
        setSortByColumn(columnIndex);
    };

    const toggleFilters = () => {
        setFiltersExpanded(!filtersExpanded);
    };

    const protocols = React.useMemo(() => {
        const uniqueProtocols = new Set(data.map(channel => formatProtocol(channel.messageSource)));
        return ['All', ...Array.from(uniqueProtocols).filter(p => p).sort()];
    }, [data]);

    const statuses = React.useMemo(() => {
        const uniqueStatuses = new Set(data.map(channel => channel.state?.toUpperCase()).filter(s => s));
        return ['All', ...Array.from(uniqueStatuses).sort()];
    }, [data]);

    const formats = React.useMemo(() => {
        const uniqueFormats = new Set(data.map(channel => formatFormat(channel.messageType)));
        return ['All', ...Array.from(uniqueFormats).filter(f => f).sort()];
    }, [data]);

    const filteredData = React.useMemo(() => {
        return data.filter(channel => {
            if (protocolFilter !== 'All' && formatProtocol(channel.messageSource) !== protocolFilter) {
                return false;
            }
            
            if (statusFilter !== 'All' && channel.state?.toUpperCase() !== statusFilter) {
                return false;
            }
            
            if (formatFilter !== 'All' && formatFormat(channel.messageType) !== formatFilter) {
                return false;
            }
            
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const nameMatch = channel.name?.toLowerCase().includes(searchLower);
                const formatMatch = formatFormat(channel.messageType)?.toLowerCase().includes(searchLower);
                return nameMatch || formatMatch;
            }
            
            return true;
        });
    }, [data, protocolFilter, statusFilter, formatFilter, searchText]);

    const sortedData = filteredData.map(channelToArray).sort(useDataSorter(sortByColumn, sortDescending));

    const clearFilters = () => {
        setProtocolFilter('All');
        setStatusFilter('All');
        setFormatFilter('All');
        setSearchText('');
    };

    const hasActiveFilters = protocolFilter !== 'All' || statusFilter !== 'All' || formatFilter !== 'All' || searchText !== '';

    const renderSort = (columnIndex: number) => {
        if (columnIndex === sortByColumn) {
            return sortDescending ? <FontAwesomeIcon icon={faCaretSquareUp} style={{ marginRight: "8px" }} /> : <FontAwesomeIcon icon={faCaretSquareDown} style={{ marginRight: "8px" }} />
        }
        return null;
    }

    return <OuterContainer>
        <TitleBar $hasFiltersExpanded={filtersExpanded}>
            <TitleText>Channels</TitleText>
                <FilterToggleButton 
                    onClick={toggleFilters}
                    title={filtersExpanded ? "Hide filters" : "Show filters"}
                    aria-label={filtersExpanded ? "Hide filters" : "Show filters"}
                >
                    <FontAwesomeIcon icon={faSlidersH} />
                </FilterToggleButton>

        </TitleBar>

        <FilterPanel $isExpanded={filtersExpanded}>
            <FilterContainer>
                <FilterLabel>
                    Protocol:
                    <FilterDropdown 
                        value={protocolFilter} 
                        onChange={(e) => setProtocolFilter(e.target.value)}
                    >
                        {protocols.map(protocol => (
                            <option key={protocol} value={protocol}>{protocol}</option>
                        ))}
                    </FilterDropdown>
                </FilterLabel>
                
                <FilterLabel>
                    Status:
                    <FilterDropdown 
                        value={statusFilter} 
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        {statuses.map(status => (
                            <option key={status} value={status}>
                                {status === 'All' ? 'All' : status.charAt(0) + status.slice(1).toLowerCase()}
                            </option>
                        ))}
                    </FilterDropdown>
                </FilterLabel>
                
                <FilterLabel>
                    Format:
                    <FilterDropdown 
                        value={formatFilter} 
                        onChange={(e) => setFormatFilter(e.target.value)}
                    >
                        {formats.map(format => (
                            <option key={format} value={format}>{format}</option>
                        ))}
                    </FilterDropdown>
                </FilterLabel>
                
                <FilterLabel>
                    <FilterInput 
                        type="text"
                        placeholder="Search text"
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                    />
                </FilterLabel>
                
                {hasActiveFilters && (
                    <ClearFiltersButton onClick={clearFilters} title="Clear all filters">
                        Clear filters
                    </ClearFiltersButton>
                )}
            </FilterContainer>
        </FilterPanel>
        
        <InnerContainer>
            <Table>
                <thead>
                    <ItemLine>
                        <ItemHeaderCell width={"30%"} onClick={changeSortColumn(0)}>Channel Name {renderSort(0)}</ItemHeaderCell>
                        <ItemHeaderCell width={"12%"} onClick={changeSortColumn(1)}>Status {renderSort(1)}</ItemHeaderCell>
                        <ItemHeaderCell width={"10%"} onClick={changeSortColumn(4)}>Alerts {renderSort(4)}</ItemHeaderCell>
                        <ItemHeaderCell width={"10%"} onClick={changeSortColumn(5)}>Inbound {renderSort(5)}</ItemHeaderCell>
                        <ItemHeaderCell width={"10%"} onClick={changeSortColumn(6)}>Processing {renderSort(6)}</ItemHeaderCell>
                        <ItemHeaderCell width={"10%"} onClick={changeSortColumn(7)}>Outbound {renderSort(7)}</ItemHeaderCell>
                        <ItemHeaderCell width={"10%"} onClick={changeSortColumn(8)}>Error {renderSort(8)}</ItemHeaderCell>
                    </ItemLine>
                </thead>
                <tbody>
                    {sortedData.map(channelToItem)}
                </tbody>
            </Table>
        </InnerContainer>
    </OuterContainer>;
};

export default QVeraChannels;
